import { createEntityAdapter } from "@reduxjs/toolkit";
import { first, isString, last } from "lodash-es";

import { asyncSlice } from "./slice";

function camelToSnake(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function camelToKebab(str) {
  return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function _processValue(value, options) {
  const { caseSensitive } = options || {}; // case-insensitive by default
  if (!caseSensitive && isString(value)) {
    return value.toLowerCase();
  }
  return value;
}

function _filter(filters, options) {
  const keyValues = Object.entries(filters).map(([key, value]) => [
    key,
    key === "classId" ? "klass_id" : camelToSnake(key),
    // TODO: currently the monitoring app has kebab-case attributes. We can get rid of the
    // camelToKebab conversion when we switch to regular snake-case attributes.
    key === "classId" ? "klass-id" : camelToKebab(key),
    value,
  ]);
  return (obj) => {
    return keyValues.reduce((accumulator, [key, snakeKey, kebabKey, value]) => {
      if (value === null) return accumulator;
      if (!(key in obj) && !(snakeKey in obj) && !(kebabKey in obj)) return false;
      const processedValue = _processValue(value, options);
      if (key in obj && _processValue(obj[key], options) !== processedValue) return false;
      if (snakeKey in obj && _processValue(obj[snakeKey], options) !== processedValue) return false;
      if (kebabKey in obj && _processValue(obj[kebabKey], options) !== processedValue) return false;
      return accumulator;
    }, true);
  };
}

// Creates a fully functional redux store with a set of common reducers and common selectors
//
// @param {string} name:
//
//   the name of the store. The data in this store will be located under
//   state.<NAME> (for example, state.user, state.course, ...)
//
// @param {any} initalState:
//
//   the initial data for the store. Most of our stores have a common data structure:
//   {
//     data:{
//       ids: [ ... ],
//       entities: { id1: {}, id2: {}, ... },
//     },
//     loading: ...,
//     error: ...,
//   }
//
//   The reducers and selectors described below rely on this data structure. However, it is possible to use
//   a different data structure for non-model related stores.
//
// @params {object} adapter:
//
//   Custom adapter created by createEntityAdapter. One reason to use a custom adapter is to be able to set
//   the default order of the ids returned by selectIds and selectAll. To do this, you can specify `sortComparer`
//   in the createEntityAdapter call.
//
// @params {object} reducers:
//
//   Custom reducers that can be used in addition to the reducers described below. for example:
//   ...
//   reducers: {
//     setBlah: (draft, action) => {
//       draft.data.blah = action.payload
//     },
//   },
//   ...
//
// @params {object} customSelectors:
//
//   Custom selectors that can be used in addition to the selectors described below. for example:
//   ...
//   customSelectors: {
//     selectValid: (selectors, state) => {
//       const allObjects = selectors.selectAll(state);
//       return allObjects.find((obj) => isValid(obj));
//     },
//     selectByName: (selectors, state, name) => {
//       const allObjects = selectors.selectAll(state);
//       return allObjects.find((obj) => checkSomethingWithName(name));
//     },
//   },
//   ...
//   In this pattern,
//     - selectors refer to the base selectors defined by default in the store (see below).
//     - state refers to the state of the global store
//
// @return {object} with the following structure:
//   Store = {
//     actions: {
//       set:
//         sets state.<NAME>.data
//       setError:
//         sets state.<NAME>.error
//       setLoading:
//         sets state.<NAME>.loading to true
//       unsetLoading:
//         sets state.<NAME>.loading to false
//       update:
//         updates state.<NAME>.data
//
//       addOne:
//         adds an object to state.<NAME>.data.entities and its id to state.<NAME>.data.ids
//       addMany:
//         adds multiple objects to state.<NAME>.data.entities and their ids to state.<NAME>.data.ids
//       setAll:
//         sets all objects in state.<NAME>.data.entities and all ids in state.<NAME>.data.ids
//       removeOne:
//         removes an object from state.<NAME>.data.entities and its id from state.<NAME>.data.ids
//       removeMany:
//         removed multiple objects from state.<NAME>.data.entities and their ids from state.<NAME>.data.ids
//       updateOne:
//         updates one object in state.<NAME>.data.entities
//       updateMany:
//         updates multiple objects in state.<NAME>.data.entities
//       upsertOne:
//         updates one object in state.<NAME>.data.entities or creates it if it does not exists
//       upsertMany:
//         updates multiple objects in state.<NAME>.data.entities or creates them if they do not exist
//     },
//     selectors: {
//       select:
//         returns the content of state.<NAME>.data
//       selectError:
//         returns the content of state.<NAME>.error: typically an error or error message or null;
//         was there an error when fetching the data?
//       selectLoading:
//         returns the content of state.<NAME>.loading: typically a boolean;
//         is the data being loaded or not?
//       selectAll:
//         returns an array containing all data entities sorted using `sortComparer` (see createEntityAdapter)
//       selectById:
//         returns one object with matching id or undefined
//       selectEntities:
//         returns an object with all data entities (where the keys are the ids)
//       selectIds:
//         returns an array containing all ids sorted using `sortComparer` (see createEntityAdapter)
//       filter:
//         returns an array containing all data entities sorted using `sortComparer` (see createEntityAdapter)
//           and filtered by a given attribute (see Filtering below for details)
//       find:
//         returns an object with matching attribute or undefined (see Filtering below for details)
//     },
//     sliceSelectors:
//       same as above selectors except that they all return an object with the following structure: {
//         data: ...,
//         error: ...,
//         loading: ...
//       }
//   }
//
// Filtering:
// The `filter` and `find` selectors allow to filter data by a given field or a combination of fields.
// Filtering works for any field (camelCase or snakeCase) of the data stored in the store.
// For example, suppose you have a store with the following data:
// [
//   {
//     id: 1,
//     user_id: 3,
//     name: "blah",
//     ...
//   },
//   {
//     id: 2,
//     user_id: 3,
//     name: "tiktok",
//     ...
//   },
//   {
//     id: 3,
//     user_id: 7,
//     name: "TikTok"
//     ...
//   },
// ]
//
// MyStore.selectors.filter({ user_id: 3 }) will yield  `[{ id: 1, ... }, { id: 2, ...}]`
// MyStore.selectors.filter({ userId: 3 })  will yield  `[{ id: 1, ... }, { id: 2, ...}]`
// MyStore.selectors.filter({ user_id: 7 }) will yield  `[{ id: 3, ...}]`
// MyStore.selectors.find({ user_id: 7 })   will yield  `{ id: 3, ...}`
//
// By default, string matching is case-insensitive. But it can be made case-sensitive with the caseSensitive option.
// For example,
//
// MyStore.selectors.filter({ name: "tiktok" })                          will yield `[{ id: 2, ... }, { id: 3, ...}]`
// MyStore.selectors.filter({ name: "tiktok" }, { caseSensitive: true }) will yield `[{ id: 2, ... }]`

function createReduxStore(initParams) {
  const { name, initialState, reducers, customSelectors = {} } = initParams;
  const hasDefaultDataStructure = initParams.hasDefaultDataStructure || !initialState;
  const adapter = initParams.adapter || createEntityAdapter();
  const slice = asyncSlice({ name, adapter, initialState, reducers });

  const dataCallback = (state) => state[name].data;
  const errorCallback = (state) => state[name].error;
  const loadingCallback = (state) => state[name].loading;

  const baseSelectors = adapter.getSelectors(dataCallback);
  const additionalSelectors = Object.fromEntries(
    Object.entries(customSelectors).map(([selectorName, selectorGenerator]) => [
      selectorName,
      (params) => (state) => selectorGenerator(baseSelectors, state, params),
    ]),
  );
  const additionalSliceSelectors = Object.fromEntries(
    Object.entries(customSelectors).map(([selectorName, selectorGenerator]) => [
      selectorName,
      (params) => (state) => {
        return {
          data: selectorGenerator(baseSelectors, state, params),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
    ]),
  );

  return {
    name,
    hasDefaultDataStructure,
    ...slice,
    selectors: {
      ...baseSelectors,
      select: (property) => (state) => {
        return property ? dataCallback(state)[property] : dataCallback(state);
      },
      selectError: () => errorCallback,
      selectLoading: () => loadingCallback,
      selectAll: () => (state) => baseSelectors.selectAll(state),
      selectById: (id) => (state) => baseSelectors.selectById(state, id),
      selectEntities: () => (state) => baseSelectors.selectEntities(state),
      selectIds: () => (state) => baseSelectors.selectIds(state),
      selectFirst: () => (state) => first(baseSelectors.selectAll(state) || []),
      selectLast: () => (state) => last(baseSelectors.selectAll(state) || []),
      filter: (payload, options) => (state) =>
        baseSelectors.selectAll(state).filter(_filter(payload, options)),
      find: (payload, options) => (state) =>
        baseSelectors.selectAll(state).find(_filter(payload, options)),
      ...additionalSelectors,
    },
    sliceSelectors: {
      select: (property) => (state) => {
        return {
          data: property ? dataCallback(state)[property] : dataCallback(state),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      selectAll: () => (state) => {
        return {
          data: baseSelectors.selectAll(state),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      selectById: (id) => (state) => {
        return {
          data: baseSelectors.selectById(state, id),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      selectEntities: () => (state) => {
        return {
          data: baseSelectors.selectEntities(state),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      selectIds: () => (state) => {
        return {
          data: baseSelectors.selectIds(state),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      selectFirst: () => (state) => {
        return {
          data: first(baseSelectors.selectAll(state) || []),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      selectLast: () => (state) => {
        return {
          data: last(baseSelectors.selectAll(state) || []),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      filter: (payload, options) => (state) => {
        return {
          data: baseSelectors.selectAll(state).filter(_filter(payload, options)),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      find: (payload, options) => (state) => {
        return {
          data: baseSelectors.selectAll(state).find(_filter(payload, options)),
          error: errorCallback(state),
          loading: loadingCallback(state),
        };
      },
      ...additionalSliceSelectors,
    },
  };
}

export { createReduxStore };
